import type { Instruction, ProgramIR, OpCode } from '../../plc/runtime/ir';
import type { BooleanExpression } from './expression';
import { compileNetworkLogic } from './graph';
import type { LadderProgram } from '../model/LadderProgram';
import { parseAddress, parseOperand } from '../../plc/address/parser';
import { parseTime } from '../../plc/core/timeParser';

export interface CompileDiagnostic {
  level: 'error' | 'warning' | 'info';
  message: string;
  networkId?: string;
  elementId?: string;
}

export interface CompileResult {
  success: boolean;
  ir: ProgramIR;
  diagnostics: CompileDiagnostic[];
}

export function compileLadderProgram(program: LadderProgram): CompileResult {
  const diagnostics: CompileDiagnostic[] = [];
  const ir: Instruction[] = [];
  let success = true;

  for (const network of program.networks) {
    try {
      const logic = compileNetworkLogic(network);

      for (const coil of logic.coils) {
        let addr = null;
        if (coil.address) {
          addr = parseAddress(coil.address);
          if (!addr) {
            diagnostics.push({
              level: 'error',
              message: `Geçersiz adres: ${coil.address}`,
              networkId: network.id,
              elementId: coil.elementId,
            });
            success = false;
            continue;
          }
        }

        if (!['ADD_I', 'SUB_I', 'MUL_I', 'DIV_I', 'ADD_R', 'SUB_R', 'MUL_R', 'DIV_R', 'MOV_W', 'MOV_R', 'SCALE', 'JMP', 'LBL'].includes(coil.type)) {
          if (!addr) {
            diagnostics.push({
              level: 'error',
              message: `Geçersiz adres: ${coil.address ?? ''}`,
              networkId: network.id,
              elementId: coil.elementId,
            });
            success = false;
            continue;
          }
        }

        if (coil.type !== 'CTUD' && coil.type !== 'CTD' && coil.type !== 'CTU' && coil.type !== 'SR' && coil.type !== 'RS' && coil.type !== 'TONR' && coil.type !== 'LBL') {
          const condIr = compileExpression(coil.condition, diagnostics, network.id, false);
          ir.push(...condIr);
        }

        switch (coil.type as string) {
          case 'COIL':
            ir.push({ op: 'OUT', address: addr!, sourceElementId: coil.elementId });
            break;
          case 'INVERT_COIL':
            ir.push({ op: 'OUTN', address: addr!, sourceElementId: coil.elementId });
            break;
          case 'SET_COIL':
            ir.push({ op: 'SET', address: addr!, sourceElementId: coil.elementId });
            break;
          case 'RESET_COIL':
            ir.push({ op: 'RESET', address: addr!, sourceElementId: coil.elementId });
            break;
          case 'JMP':
            ir.push({ op: 'JMP', label: coil.label ?? '', sourceElementId: coil.elementId });
            break;
          case 'LBL':
            ir.push({ op: 'LBL', label: coil.label ?? '', sourceElementId: coil.elementId });
            break;
          case 'SR': {
            ir.push(...compileExpression(coil.condition, diagnostics, network.id, false));
            ir.push({ op: 'SR_SET', address: addr!, sourceElementId: coil.elementId });
            if (coil.condition2) {
              ir.push(...compileExpression(coil.condition2, diagnostics, network.id, false));
              ir.push({ op: 'SR_RESET', address: addr!, sourceElementId: coil.elementId });
            }
            break;
          }
          case 'RS': {
            ir.push(...compileExpression(coil.condition, diagnostics, network.id, false));
            ir.push({ op: 'RS_SET', address: addr!, sourceElementId: coil.elementId });
            if (coil.condition2) {
              ir.push(...compileExpression(coil.condition2, diagnostics, network.id, false));
              ir.push({ op: 'RS_RESET', address: addr!, sourceElementId: coil.elementId });
            }
            break;
          }
          case 'TONR': {
            const ptMs = coil.value ? parseTime(coil.value) : null;
            if (ptMs === null || ptMs < 0) {
              diagnostics.push({
                level: 'error',
                message: `Geçersiz Preset Time: ${coil.value}`,
                networkId: network.id,
                elementId: coil.elementId,
              });
              success = false;
            } else {
              ir.push(...compileExpression(coil.condition, diagnostics, network.id, false));
              ir.push({ op: 'TONR', address: addr!, sourceElementId: coil.elementId, value: ptMs });
              if (coil.condition2) {
                ir.push(...compileExpression(coil.condition2, diagnostics, network.id, false));
                ir.push({ op: 'TONR_RESET', address: addr!, sourceElementId: coil.elementId });
              }
            }
            break;
          }
          case 'TON':
          case 'TOF':
          case 'TP': {
            const ptMs = coil.value ? parseTime(coil.value) : null;
            if (ptMs === null || ptMs < 0) {
              diagnostics.push({
                level: 'error',
                message: `Geçersiz Preset Time: ${coil.value}`,
                networkId: network.id,
                elementId: coil.elementId,
              });
              success = false;
            } else {
              ir.push({ op: coil.type as OpCode, address: addr!, sourceElementId: coil.elementId, value: ptMs });
            }
            break;
          }
          case 'CTU':
          case 'CTD':
          case 'CTUD': {
            const pv = coil.value ? parseInt(coil.value, 10) : null;
            if (pv === null || isNaN(pv) || pv < 0) {
              diagnostics.push({
                level: 'error',
                message: `Geçersiz Preset Value: ${coil.value}`,
                networkId: network.id,
                elementId: coil.elementId,
              });
              success = false;
            } else {
              if (coil.type === 'CTUD') {
                ir.push(...compileExpression(coil.condition, diagnostics, network.id, false));
                ir.push({ op: 'CTUD_CU', address: addr!, sourceElementId: coil.elementId, value: pv });
                if (coil.condition2) {
                  ir.push(...compileExpression(coil.condition2, diagnostics, network.id, false));
                  ir.push({ op: 'CTUD_CD', address: addr!, sourceElementId: coil.elementId, value: pv });
                }
              } else if (coil.type === 'CTU') {
                ir.push(...compileExpression(coil.condition, diagnostics, network.id, false));
                ir.push({ op: 'CTU', address: addr!, sourceElementId: coil.elementId, value: pv });
                if (coil.condition2) {
                  ir.push(...compileExpression(coil.condition2, diagnostics, network.id, false));
                  ir.push({ op: 'CTU_RESET', address: addr!, sourceElementId: coil.elementId });
                }
              } else if (coil.type === 'CTD') {
                ir.push(...compileExpression(coil.condition, diagnostics, network.id, false));
                ir.push({ op: 'CTD', address: addr!, sourceElementId: coil.elementId, value: pv });
                if (coil.condition2) {
                  ir.push(...compileExpression(coil.condition2, diagnostics, network.id, false));
                  ir.push({ op: 'CTD_LOAD', address: addr!, sourceElementId: coil.elementId });
                }
              }
            }
            break;
          }
          case 'ADD_I':
          case 'SUB_I':
          case 'MUL_I':
          case 'DIV_I':
          case 'ADD_R':
          case 'SUB_R':
          case 'MUL_R':
          case 'DIV_R': {
            ir.push(...compileExpression(coil.condition, diagnostics, network.id, false));
            const op1 = parseOperand(coil.in1 ?? '');
            const op2 = parseOperand(coil.in2 ?? '');
            const outAddr = parseAddress(coil.out ?? '');
            if (op1 && op2 && outAddr) {
              ir.push({ op: coil.type as OpCode, address: outAddr, sourceElementId: coil.elementId, operands: [op1, op2] });
            } else {
              diagnostics.push({ level: 'error', message: `Geçersiz operand: ${coil.type}`, networkId: network.id });
            }
            break;
          }
          case 'MOV_W':
          case 'MOV_R': {
            ir.push(...compileExpression(coil.condition, diagnostics, network.id, false));
            const opIn = parseOperand(coil.in ?? '');
            const outAddr = parseAddress(coil.out ?? '');
            if (opIn && outAddr) {
              ir.push({ op: coil.type as OpCode, address: outAddr, sourceElementId: coil.elementId, operands: [opIn] });
            } else {
              diagnostics.push({ level: 'error', message: `Geçersiz operand: ${coil.type}`, networkId: network.id });
            }
            break;
          }
          case 'SCALE': {
            ir.push(...compileExpression(coil.condition, diagnostics, network.id, false));
            const opIn = parseOperand(coil.in ?? '');
            const opInMin = parseOperand(coil.inMin ?? '0');
            const opInMax = parseOperand(coil.inMax ?? '27648');
            const opOutMin = parseOperand(coil.outMin ?? '0');
            const opOutMax = parseOperand(coil.outMax ?? '100');
            const outAddr = parseAddress(coil.out ?? '');
            if (opIn && opInMin && opInMax && opOutMin && opOutMax && outAddr) {
              ir.push({
                op: 'SCALE',
                address: outAddr,
                sourceElementId: coil.elementId,
                operands: [opIn],
                scaleParams: { inMin: opInMin, inMax: opInMax, outMin: opOutMin, outMax: opOutMax },
              });
            } else {
              diagnostics.push({ level: 'error', message: `Geçersiz operand: SCALE`, networkId: network.id });
            }
            break;
          }
        }
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      diagnostics.push({
        level: 'error',
        message: `Network ${network.id} derlenirken hata: ${msg}`,
        networkId: network.id,
      });
      success = false;
    }
  }

  return { success, ir, diagnostics };
}

function compileExpression(
  expr: BooleanExpression,
  diagnostics: CompileDiagnostic[],
  networkId: string,
  isNested: boolean,
): Instruction[] {
  const insts: Instruction[] = [];

  switch (expr.type) {
    case 'TRUE':
      insts.push({ op: isNested ? 'ANDN' : 'LDN' }); // LDN undef = true. ANDN undef = AND true.
      break;

    case 'FALSE':
      insts.push({ op: isNested ? 'AND' : 'LD' }); // LD undef = false. AND undef = AND false.
      break;

    case 'NOT': {
      const inner = compileExpression(expr.expr, diagnostics, networkId, isNested);
      insts.push(...inner);
      insts.push({ op: 'NOT', sourceElementId: expr.elementId });
      break;
    }

    case 'EDGE_CONTACT': {
      const addr = parseAddress(expr.address);
      if (addr) {
        if (!isNested) {
          insts.push({ op: 'LDN' });
        }
        insts.push({
          op: expr.edge === 'RISING' ? 'P_CONTACT' : 'N_CONTACT',
          address: addr,
          sourceElementId: expr.elementId,
        });
      } else {
        diagnostics.push({
          level: 'error',
          message: `Geçersiz adres: ${expr.address}`,
          networkId,
          elementId: expr.elementId,
        });
      }
      break;
    }

    case 'IN_RANGE': {
      const opIn = parseOperand(expr.in);
      const opMin = parseOperand(expr.min);
      const opMax = parseOperand(expr.max);
      if (opIn && opMin && opMax) {
        if (!isNested) {
          insts.push({ op: 'LDN' });
        }
        insts.push({
          op: 'IN_RANGE',
          operands: [opIn, opMin, opMax],
          sourceElementId: expr.elementId,
        });
      }
      break;
    }

    case 'CONTACT': {
      const addr = parseAddress(expr.address);
      if (!addr) {
        diagnostics.push({
          level: 'error',
          message: `Geçersiz adres: ${expr.address}`,
          networkId,
          elementId: expr.elementId,
        });
        break;
      }
      insts.push({
        op: isNested
          ? expr.isNormallyClosed
            ? 'ANDN'
            : 'AND'
          : expr.isNormallyClosed
            ? 'LDN'
            : 'LD',
        address: addr,
        sourceElementId: expr.elementId,
      });
      break;
    }

    case 'COMPARE': {
      const op1 = parseOperand(expr.in1);
      const op2 = parseOperand(expr.in2);
      if (op1 && op2) {
        if (!isNested) {
          insts.push({ op: 'LDN' });
        }
        insts.push({
          op: expr.op,
          sourceElementId: expr.elementId,
          operands: [op1, op2],
        });
      } else {
        diagnostics.push({
          level: 'error',
          message: `Geçersiz operand: Compare`,
          networkId,
          elementId: expr.elementId,
        });
      }
      break;
    }

    case 'AND': {
      const left = compileExpression(expr.left, diagnostics, networkId, isNested);
      insts.push(...left);

      if (expr.right.type === 'CONTACT') {
        const addr = parseAddress(expr.right.address);
        if (addr) {
          insts.push({
            op: expr.right.isNormallyClosed ? 'ANDN' : 'AND',
            address: addr,
            sourceElementId: expr.right.elementId,
          });
        } else {
          diagnostics.push({
            level: 'error',
            message: `Geçersiz adres: ${expr.right.address}`,
            networkId,
            elementId: expr.right.elementId,
          });
        }
      } else {
        insts.push({ op: 'MPS' });
        const right = compileExpression(expr.right, diagnostics, networkId, false);
        insts.push(...right);
        insts.push({ op: 'ALD' });
      }
      break;
    }

    case 'OR': {
      const left = compileExpression(expr.left, diagnostics, networkId, isNested);
      insts.push(...left);

      if (expr.right.type === 'CONTACT') {
        const addr = parseAddress(expr.right.address);
        if (addr) {
          insts.push({
            op: expr.right.isNormallyClosed ? 'ORN' : 'OR',
            address: addr,
            sourceElementId: expr.right.elementId,
          });
        } else {
          diagnostics.push({
            level: 'error',
            message: `Geçersiz adres: ${expr.right.address}`,
            networkId,
            elementId: expr.right.elementId,
          });
        }
      } else {
        insts.push({ op: 'MPS' });
        const right = compileExpression(expr.right, diagnostics, networkId, false);
        insts.push(...right);
        insts.push({ op: 'OLD' });
      }
      break;
    }
  }

  return insts;
}
